import React, { useState, useCallback } from 'react';
import { Play, Pause, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  validateVoiceoverWordCount,
  MAX_WORDS_PER_SCENE,
  WORD_LIMIT_EXCEEDED_MESSAGE,
} from '@/constants/voiceover';
import { StatusBadge } from './PropertySceneCardShared';
import type { IVerticalVoiceoverSectionProps } from '@/@types/propertySceneCard';

function getEffectiveVoiceoverText(scene: IVerticalVoiceoverSectionProps['scene']): string {
  return scene.editedVoiceoverText ?? scene.voiceoverText;
}

export function VerticalVoiceoverSection({
  sceneId,
  scene,
  isSaving,
  isRegeneratingVoiceover,
  isPlayingAudio,
  onPlayPause,
  onRegenerateVoiceover,
  onVoiceoverTextChange,
}: IVerticalVoiceoverSectionProps) {
  const [localVoiceover, setLocalVoiceover] = useState(() => getEffectiveVoiceoverText(scene));
  const [isDirty, setIsDirty] = useState(false);

  const handleVoiceoverChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setLocalVoiceover(e.target.value);
      setIsDirty(e.target.value !== getEffectiveVoiceoverText(scene));
    },
    [scene]
  );

  const handleSave = useCallback(async () => {
    if (!isDirty) return;

    const { isOverLimit } = validateVoiceoverWordCount(localVoiceover);
    if (isOverLimit) {
      toast.warning(WORD_LIMIT_EXCEEDED_MESSAGE, { duration: 5000 });
      return;
    }

    await onVoiceoverTextChange(localVoiceover);
    setIsDirty(false);
  }, [isDirty, localVoiceover, onVoiceoverTextChange]);

  const { wordCount, isOverLimit } = validateVoiceoverWordCount(localVoiceover);

  return (
    <div className="flex-1 space-y-2">
      <label htmlFor={`scene-voiceover-${sceneId}`} className="text-sm font-medium">
        Voiceover Text:
      </label>
      <Textarea
        id={`scene-voiceover-${sceneId}`}
        value={localVoiceover}
        onChange={handleVoiceoverChange}
        rows={4}
        className="resize-none"
      />
      <p
        className={cn(
          'text-xs mt-1',
          isOverLimit ? 'text-destructive font-medium' : 'text-muted-foreground'
        )}
      >
        {wordCount}/{MAX_WORDS_PER_SCENE} words
        {isOverLimit && ' (over limit)'}
      </p>
      <div className="flex items-center gap-2">
        <StatusBadge status={scene.status} type="voiceover" />
        {scene.voiceoverAudioId && (
          <Button
            variant="outline"
            size="sm"
            onClick={onPlayPause}
            aria-label={isPlayingAudio ? 'Pause voiceover audio' : 'Play voiceover audio'}
          >
            {isPlayingAudio ? (
              <Pause className="h-4 w-4 mr-1" />
            ) : (
              <Play className="h-4 w-4 mr-1" />
            )}
            {isPlayingAudio ? 'Pause' : 'Play'}
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={onRegenerateVoiceover}
          disabled={isRegeneratingVoiceover}
          aria-label={
            scene.voiceoverAudioId ? 'Regenerate voiceover audio' : 'Generate voiceover audio'
          }
        >
          <RefreshCw className={cn('h-4 w-4 mr-1', isRegeneratingVoiceover && 'animate-spin')} />
          {scene.voiceoverAudioId ? 'Regenerate' : 'Generate'}
        </Button>
        {isDirty && (
          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </div>
    </div>
  );
}
